/**
 * usePodToday — activation hook + polling sync for Pod Today.
 * Checks all in-progress plans for Pod Today activation, manages
 * 5-second polling for real-time sync during active Pod Today.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getPodTodayStatus,
  getPodTodayActivities,
  toggleAttendance as toggleAttendanceApi,
  postActivityUpdate as postUpdateApi,
  pollPodToday,
} from '../services/podTodayApi';
import type {
  PodTodayStatus,
  PodTodayActivity,
  PodTodayParticipant,
} from '../services/podTodayApi';

const POLL_INTERVAL = 5000; // 5 seconds

interface UsePodTodayReturn {
  isActive: boolean;
  podNames: string[];
  participants: PodTodayParticipant[];
  activities: PodTodayActivity[];
  planTitle: string;
  planDestination: string;
  activePlanId: number | null;
  isLoading: boolean;
  feedRefreshKey: number;

  toggleAttendance: (itemId: number) => Promise<void>;
  postUpdate: (itemId: number, type: 'late' | 'change' | 'note', text: string, durationMinutes?: number) => Promise<void>;
  refresh: () => Promise<void>;
}

export function usePodToday(planIds: number[], date: string): UsePodTodayReturn {
  const [status, setStatus] = useState<PodTodayStatus | null>(null);
  const [activities, setActivities] = useState<PodTodayActivity[]>([]);
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);
  const lastPollTime = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check which plan (if any) activates Pod Today
  useEffect(() => {
    if (planIds.length === 0) {
      setStatus(null);
      setActivePlanId(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    // Check each plan for Pod Today activation
    (async () => {
      for (const planId of planIds) {
        try {
          const s = await getPodTodayStatus(planId);
          if (!cancelled && s.active) {
            setStatus(s);
            setActivePlanId(planId);
            // Load activities
            const data = await getPodTodayActivities(planId, date);
            if (!cancelled) {
              setActivities(data.activities);
            }
            setIsLoading(false);
            return;
          }
        } catch {
          // Plan not accessible or error — skip
        }
      }
      // No active Pod Today found
      if (!cancelled) {
        setStatus(null);
        setActivePlanId(null);
        setActivities([]);
        setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [planIds.join(','), date]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling for real-time sync
  useEffect(() => {
    if (!activePlanId || !status?.active) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const response = await pollPodToday(
          activePlanId,
          date,
          lastPollTime.current || undefined,
        );
        lastPollTime.current = response.server_time;

        // Apply attendance + state updates
        setActivities(prev => prev.map(act => {
          const update = response.activities.find(a => a.id === act.id);
          if (!update) return act;
          return {
            ...act,
            attendance: (update.attendance || act.attendance) as Record<string, 'attending' | 'opted_out'>,
            completion_state: update.completion_state || act.completion_state,
            activity_status: update.activity_status || act.activity_status,
          };
        }));

        // If there are new updates, increment feed refresh key
        if (response.new_updates.length > 0) {
          setFeedRefreshKey(k => k + 1);
        }
      } catch {
        // Poll failed — will retry on next interval
      }
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activePlanId, status?.active, date]);

  const toggleAttendance = useCallback(async (itemId: number) => {
    if (!activePlanId) return;

    // Find current status
    const activity = activities.find(a => a.id === itemId);
    if (!activity) return;


    const newStatus = activity.attendance[
      // We need the current user's ID — get from status
      String(status?.participants?.find(() => true)?.id || '')
    ] === 'attending' ? 'opted_out' as const : 'attending' as const;

    // Let server do the actual toggle
    const result = await toggleAttendanceApi(activePlanId, itemId, newStatus);

    // Apply server response
    setActivities(prev => prev.map(a =>
      a.id === itemId ? { ...a, attendance: result.attendance as Record<string, 'attending' | 'opted_out'> } : a
    ));
  }, [activePlanId, activities, status]);

  const postUpdate = useCallback(async (
    itemId: number,
    type: 'late' | 'change' | 'note',
    text: string,
    durationMinutes?: number,
  ) => {
    if (!activePlanId) return;
    await postUpdateApi(activePlanId, itemId, type, text, durationMinutes);
    setFeedRefreshKey(k => k + 1);

    // If type is 'change', update the activity's completion_state locally
    if (type === 'change') {
      setActivities(prev => prev.map(a =>
        a.id === itemId ? { ...a, completion_state: 'replaced' } : a
      ));
    }
  }, [activePlanId]);

  const refresh = useCallback(async () => {
    if (!activePlanId) return;
    const data = await getPodTodayActivities(activePlanId, date);
    setActivities(data.activities);
    setFeedRefreshKey(k => k + 1);
  }, [activePlanId, date]);

  return {
    isActive: status?.active || false,
    podNames: status?.pod_names || [],
    participants: status?.participants || [],
    activities,
    planTitle: status?.plan_title || '',
    planDestination: status?.plan_destination || '',
    activePlanId,
    isLoading,
    feedRefreshKey,
    toggleAttendance,
    postUpdate,
    refresh,
  };
}
